/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { subDays } from "date-fns";
import { Box, Grid } from "@mui/material";
import useOverview from "../../hooks/store/overview";
import Loader from "../../common/Loader";
import "rsuite/dist/rsuite.min.css";
import moment from "moment";
import * as XLSX from "xlsx";
import "./index.css";
import { OverviewSales } from "../../components/Home/overview-sales";
import { OverviewEmpty } from "../../components/Home/overview-empty-card";
import { toast } from "react-toastify";
import usePermissions from "../../hooks/usePermissions";
import WorldMapHeatmap from "../../components/Home/overview-heatmap";
import Stats from "./Stats/Stats";
import Statistics from "./Statistics/Statistics";
import DashboardFilter from "./Filter/DashboardFilter";
import ChartFilter from "./Filter/ChartFilter";
import { downloadDashboardReportPdf } from "../../utils/dashboardReportPdf";
import {
  buildBusinessAliases,
  getBusinessDisplayName,
  replaceBusinessNames,
  replaceReferralLinks,
} from "./presentationMode";

const PRESENTATION_MODE_KEY = "dashboard-report-presentation-mode";

const chartTypes = [
  {
    id: 1,
    label: "Conversations",
    value: "conversationCount",
  },
  {
    id: 2,
    label: "Staffing Hours Saved",
    value: "staffingHoursSaved",
  },
  {
    id: 3,
    label: "Average Messages",
    value: "averageMessageCount",
  },
  {
    id: 4,
    label: "Link Count",
    value: "linkCount",
  },
  {
    id: 5,
    label: "Total Message",
    value: "totalMessageCount",
  },
  {
    id: 6,
    label: "Reservation",
    value: "reservationCount",
  },
];

function Home() {
  const {
    getOverallBussiness,
    getBusinessActions,
    getChartDataAction,
    businesOptions,
    overAllDatas,
    uiWidget,
    overviewChartData,
    resetData,
    getBusinessClientAction,
    getBusinessAction,
    businessName,
    getOverviewReportAction,
    getMainDashboard,
    topCategories,
    trends,
    flaggedConcerns,
  } = useOverview();

  const {
    capabilities,
    businessId: scopedBusinessId,
    canAccessBusiness,
    clientId,
    isBusiness,
    isClient,
  } = usePermissions();
  const rawBusinessOptions = useMemo(() => {
    if (Array.isArray(businesOptions)) return businesOptions;
    if (Array.isArray(businesOptions?.data)) return businesOptions.data;
    if (Array.isArray(businesOptions?.businesses)) return businesOptions.businesses;
    return [];
  }, [businesOptions]);

  const memoizedBusinessOptions = useMemo(
    () =>
      rawBusinessOptions
        .map((business) => ({
          ...business,
          businessId:
            business?.businessId || business?.id || business?.businessID || "",
          businessName:
            business?.businessName || business?.name || business?.business || "",
        }))
        .filter((business) => business.businessId || business.businessName),
    [rawBusinessOptions]
  );

  const memoizedOverAllData = useMemo(() => overAllDatas || {}, [overAllDatas]);
  const isDashboardReportLoading = Boolean(
    uiWidget?.loading || uiWidget?.dashboardLoding
  );

  const [businessId, setBusinessId] = useState(null);
  const [reportFilters, setReportFilters] = useState(null);
  const [chartInitialValue, setChartInitialValue] = useState({
    businessId: "123",
    chartType: {},
    dateRange: [subDays(new Date(), 6), new Date()],
    startDate: null,
    endDate: null,
  });
  const [selectedBusinessName, setSelectedBusinessName] = useState("");
  const [presentationMode, setPresentationMode] = useState(() => {
    try {
      return window.sessionStorage.getItem(PRESENTATION_MODE_KEY) === "1";
    } catch (_) {
      return false;
    }
  });
  const businessAliases = useMemo(
    () => buildBusinessAliases(
      reportFilters?.businessId
        ? [...memoizedBusinessOptions, { businessId: reportFilters.businessId }]
        : memoizedBusinessOptions,
      scopedBusinessId
    ),
    [memoizedBusinessOptions, reportFilters?.businessId, scopedBusinessId]
  );
  const getBusinessLabel = useCallback(
    (business) => getBusinessDisplayName(business, businessAliases, presentationMode),
    [businessAliases, presentationMode]
  );
  const displayedReportText = useMemo(() => {
    if (!presentationMode) {
      return { topCategories, trends, flaggedConcerns };
    }

    const selectedName = selectedBusinessName || (
      String(scopedBusinessId) === String(reportFilters?.businessId)
        ? businessName
        : ""
    );
    const knownBusinesses = selectedName && reportFilters?.businessId
      ? [
          ...memoizedBusinessOptions,
          { businessId: reportFilters.businessId, businessName: selectedName },
        ]
      : memoizedBusinessOptions;

    return {
      topCategories: replaceBusinessNames(topCategories, knownBusinesses, businessAliases),
      trends: replaceBusinessNames(trends, knownBusinesses, businessAliases),
      flaggedConcerns: replaceBusinessNames(flaggedConcerns, knownBusinesses, businessAliases),
    };
  }, [presentationMode, topCategories, trends, flaggedConcerns, selectedBusinessName, businessName, scopedBusinessId, reportFilters?.businessId, memoizedBusinessOptions, businessAliases]);
  const displayedReferralLinks = useMemo(
    () => presentationMode
      ? replaceReferralLinks(memoizedOverAllData?.clickedLinkList)
      : memoizedOverAllData?.clickedLinkList,
    [presentationMode, memoizedOverAllData]
  );

  const handlePresentationModeChange = (enabled) => {
    setPresentationMode(enabled);
    try {
      window.sessionStorage.setItem(PRESENTATION_MODE_KEY, enabled ? "1" : "0");
    } catch (_) {
      // The switch still works when browser storage is unavailable.
    }
  };

  useEffect(() => {
    resetData();
    if (isClient && clientId) {
      getBusinessClientAction({
        clientId,
      });
    } else if (isBusiness && scopedBusinessId) {
      getBusinessAction({ businessId: scopedBusinessId });
    } else {
      getBusinessActions();
    }
  }, []);

  const handleGetBussiness = useCallback(
    (values) => {
      setChartInitialValue({
        businessId: "123",
        chartType: {},
        dateRange: [subDays(new Date(), 6), new Date()],
        startDate: null,
        endDate: null,
      });

      let startDate = moment(values?.startDate)?.format("YYYY-MM-DD");
      let endDate = moment(values?.endDate)?.format("YYYY-MM-DD");
      const resolvedBusinessId =
        (isBusiness && scopedBusinessId) ||
        values?.business?.businessId ||
        values?.business?.id;

      if (!resolvedBusinessId) {
        toast.error("Please Select business");
        return;
      }

      if (!canAccessBusiness(resolvedBusinessId)) {
        toast.error("You do not have access to this business");
        return;
      }

      setBusinessId(resolvedBusinessId);
      setReportFilters({
        businessId: resolvedBusinessId,
        startDate,
        endDate,
      });
      setSelectedBusinessName(
        values?.business?.businessName || values?.business?.name || ""
      );
      getOverallBussiness({
        businessId: resolvedBusinessId,
        startDate: startDate,
        endDate: endDate,
      });
      if (capabilities?.canViewDashboardReports) {
        getMainDashboard({ businessId: resolvedBusinessId, startDate, endDate });
      }
    },
    [
      capabilities,
      canAccessBusiness,
      getMainDashboard,
      getOverallBussiness,
      isBusiness,
      scopedBusinessId,
    ]
  );

  const handleGetChartDetails = async (values) => {
    let startDate = moment(values?.startDate)?.format("YYYY-MM-DD");
    let endDate = moment(values?.endDate)?.format("YYYY-MM-DD");

    if (businessId) {
      setChartInitialValue({
        businessId: businessId,
        chartType: values?.chartType,
        dateRange: values?.dateRange,
        startDate: values?.startDate,
        endDate: values?.endDate,
      });

      await getChartDataAction({
        businessId: businessId,
        chartType: values?.chartType?.value,
        startDate: startDate,
        endDate: endDate,
      });
    } else {
      toast.error("Please Select business");
    }
  };

  const exportReportFiles = async () => {
    if (!reportFilters?.businessId) {
      toast.error("Build a report before exporting");
      return;
    }

    const result = await getOverviewReportAction(reportFilters);
    const reportRows = Array.isArray(result?.payload) ? result.payload : [];

    if (result?.error) {
      return;
    }

    if (!reportRows.length) {
      toast.error("No conversation data found");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(reportRows);
    worksheet["!cols"] = [{ wch: 36 }, { wch: 40 }, { wch: 80 }, { wch: 24 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Conversational Report");
    const matchingBusiness = memoizedBusinessOptions.find(
      (businessOption) =>
        String(businessOption.businessId) === String(reportFilters.businessId)
    );
    const business =
      selectedBusinessName ||
      matchingBusiness?.businessName ||
      matchingBusiness?.name ||
      businessName ||
      "business";
    const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");

    const safeBusinessName =
      String(business)
        .replace(/[^a-zA-Z0-9-_]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "") || "business";

    const fileName = `${safeBusinessName}_conversational-report_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    downloadDashboardReportPdf(
      {
        businessName: business,
        reportFilters,
        overviewData: memoizedOverAllData,
        topCategories,
        trends,
        flaggedConcerns,
      },
      `${safeBusinessName}_dashboard-summary_${timestamp}.pdf`
    );
  };

  return (
    <Box component="main" p={2}>
      {(uiWidget?.loading ||
        uiWidget?.chartLoading ||
        uiWidget.dashboardLoding) && <Loader />}
      <DashboardFilter
        businesOptions={memoizedBusinessOptions}
        getBusinessLabel={getBusinessLabel}
        presentationMode={presentationMode}
        onPresentationModeChange={handlePresentationModeChange}
        onApplyFilter={handleGetBussiness}
        showExport={
          Boolean(capabilities?.canExportTranscripts) &&
          Boolean(reportFilters?.businessId) &&
          !isDashboardReportLoading
        }
        onExportReport={exportReportFiles}
      />
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4} lg={2}>
          <Stats data={memoizedOverAllData} />
        </Grid>
        <Grid item xs={12} md={8} lg={5}>
          <WorldMapHeatmap heatMapData={memoizedOverAllData?.heatMap || []} />
        </Grid>
        <Grid item xs={12} md={12} lg={5}>
          <Statistics
            showDashboardReports={Boolean(capabilities?.canViewDashboardReports)}
            linkData={displayedReferralLinks}
            topCategories={displayedReportText.topCategories}
            trends={displayedReportText.trends}
            flaggedConcerns={displayedReportText.flaggedConcerns}
          />
        </Grid>
      </Grid>
      {uiWidget?.chartUi && (
        <>
          <ChartFilter
            chartTypes={chartTypes}
            handleGetChartDetails={handleGetChartDetails}
          />
          <Grid container xl>
            <Grid item lg={12} sm={12} md={12}>
              {overviewChartData && overviewChartData?.length >= 1 ? (
                <OverviewSales
                  chartName={chartInitialValue?.chartType}
                  chartSeries={overviewChartData || []}
                  sx={{ height: "100%" }}
                />
              ) : (
                <OverviewEmpty sx={{ height: "50vh" }} />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

export default Home;
